import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Constant } from "../../../../domain/constants";
import IconClosed from "../../../assets/iconClosed";
import "../../../assets/saas/organisms/_modal-location.scss";
import { container } from "tsyringe";
import IconLocationBlack from "../../../assets/iconLocationBlack";
import IconAirportBlack from "../../../assets/iconAirportBlack";
import IconPortBlack from "../../../assets/iconPortBlack";
import BlueBar from "presentation/components/commons/blueBar";
import { isTestMarketCountry } from "../../../../utils/testMarket";
import { findTranslation } from "../../../../infrastructure/i18n/findTranslation";
import { getUrlLocale } from "../../../../utils/getUrlLocale";
const BodyModalLocations = ({ setLocationError, selectedLocation, setSelectedLocation, handleLocationChange, locations, setLocations, type, isOpen, openModal, t, userCountry, checked, setSelectedReturnLocation, }) => {
    const [noResults, setNoResults] = useState(false);
    const [firstRender, setFirstRender] = useState(true);
    const [showValue, setShowValue] = useState(selectedLocation[0]);
    const [viewportHeight, setViewportHeight] = useState(window.innerHeight);
    const inputRef = useRef(null);
    const getLocationsByCode = container.resolve("iUseCaseGetLocationByCode");
    const isPickup = type === "pickup";
    const isTestMarket = isTestMarketCountry(userCountry);
    const pickupLocationCodeDefault = sessionStorage.getItem("pickupLocationCode") || process.env.REACT_APP_DEFAULT_LOCATION_CODE;
    useEffect(() => {
        const handleResize = () => {
            if (window.visualViewport) {
                setViewportHeight(window.visualViewport.height);
            }
        };
        if (window.visualViewport) {
            window.visualViewport.addEventListener("resize", handleResize);
            window.visualViewport.addEventListener("scroll", handleResize);
        }
        return () => {
            if (window.visualViewport) {
                window.visualViewport.removeEventListener("resize", handleResize);
                window.visualViewport.removeEventListener("scroll", handleResize);
            }
        };
    }, []);
    // Helper Functions
    const capitalizeFirstLetterAfterSpace = (text) => {
        return text
            .split(" ")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
    };
    const normalizeStorageValue = (value) => {
        if (value == null)
            return "";
        const stringValue = String(value).trim();
        if (stringValue === "" ||
            stringValue.toLowerCase() === "undefined" ||
            stringValue.toLowerCase() === "null") {
            return "";
        }
        return stringValue;
    };
    const setSessionValue = (key, value) => {
        const normalized = normalizeStorageValue(value);
        if (normalized) {
            sessionStorage.setItem(key, normalized);
        }
        else {
            sessionStorage.removeItem(key);
        }
    };
    // WI #8320 — Mobile/modal counterpart to `checkTranlation` in
    // `locationInputSearchBar.tsx`. Uses the URL locale instead of
    // `i18next.language` to avoid country-selector contamination.
    const checkTranlation = (location) => {
        var _a, _b;
        let nameTranslation = (_b = (_a = location === null || location === void 0 ? void 0 : location.name) !== null && _a !== void 0 ? _a : location === null || location === void 0 ? void 0 : location.description) !== null && _b !== void 0 ? _b : "";
        if (!Array.isArray(location === null || location === void 0 ? void 0 : location.translations)) {
            return nameTranslation;
        }
        const match = findTranslation(location.translations, getUrlLocale("lower"));
        if (match && typeof match.name === "string" && match.name.trim() !== "") {
            nameTranslation = match.name;
        }
        if (typeof nameTranslation === "string" && nameTranslation.length > 0) {
            return nameTranslation.charAt(0).toUpperCase() + nameTranslation.slice(1);
        }
        return "";
    };
    const getLocationIcon = (locationType) => {
        if (!locationType)
            return _jsx(IconLocationBlack, {});
        const iconMap = {
            [Constant.AIRPORT]: _jsx(IconAirportBlack, {}),
            [Constant.PORT]: _jsx(IconPortBlack, {}),
        };
        return iconMap[locationType] || _jsx(IconLocationBlack, {});
    };
    const closeModal = async (location) => {
        const locationCode = isTestMarket ? location.description : location.code;
        const codeA = isPickup ? locationCode : pickupLocationCodeDefault;
        const codeB = !isPickup ? locationCode : sessionStorage.getItem("returnLocationCode");
        if (isPickup) {
            setSessionValue("pickCountry", location.country);
            if (isTestMarket) {
                setSessionValue("pickPlaceId", location.placeId);
                setSessionValue("pickDataSource", location.dataSource);
            }
            sessionStorage.setItem("pickupLocationCode", locationCode);
        }
        else {
            setSessionValue("dropCountry", location.country);
            if (isTestMarket) {
                setSessionValue("dropPlaceId", location.placeId);
                setSessionValue("dropDataSource", location.dataSource);
                sessionStorage.setItem("dropPlaceId", location.placeId || "");
            }
            sessionStorage.setItem("returnLocationCode", locationCode);
        }
        const resolvedLocation = isTestMarket
            ? [location.description, location.description, location.placeType]
            : [checkTranlation(location), location.code, location.location_type];
        setSelectedLocation(resolvedLocation);
        // Mirror pickup → return when "drop off at different location" is unchecked
        if (isPickup && !checked && setSelectedReturnLocation) {
            setSelectedReturnLocation(resolvedLocation);
            sessionStorage.setItem("returnLocation", resolvedLocation[0]);
            sessionStorage.setItem("returnLocationCode", resolvedLocation[1]);
        }
        setLocations([""]);
        isOpen(false);
        setLocationError(false);
        try {
            const data = await getLocationsByCode.formatedd(codeA);
            if (data) {
                setLocations([data]);
                // Si data es un array, accede al primer elemento
                const locationData = Array.isArray(data) ? data[0] : data;
                const country = (locationData === null || locationData === void 0 ? void 0 : locationData.country) || "";
                if (isPickup) {
                    sessionStorage.setItem("pickCountry", country);
                }
                else {
                    const existingPickCountry = sessionStorage.getItem("pickCountry") || "";
                    sessionStorage.setItem("pickCountry", existingPickCountry);
                    sessionStorage.setItem("dropCountry", country);
                }
            }
            else {
                setLocations([""]);
            }
            const returnCode = sessionStorage.getItem("returnLocationCode");
            if (returnCode && returnCode !== "" && returnCode !== "undefined") {
                const secondaryData = await getLocationsByCode.formatedd(codeB);
                if (secondaryData) {
                    setLocations((prevLocations) => [...prevLocations, secondaryData]);
                }
            }
        }
        catch (error) {
            console.error("Error fetching locations:", error);
            setLocations([""]);
        }
    };
    const handleDefaultClick = () => {
        const originalCode = isPickup
            ? sessionStorage.getItem("originalPickUpCode")
            : sessionStorage.getItem("originalReturnCode");
        if (firstRender && selectedLocation[1] === originalCode) {
            setShowValue("");
            setFirstRender(false);
            setLocations([""]);
        }
    };
    const handleClearLocation = () => {
        setLocations([""]);
        setSelectedLocation(["", "", Constant.DEFAULT]);
        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }, 0);
    };
    const [hoveredLocation, setHoveredLocation] = useState(null);
    // Effects
    useEffect(() => {
        if (openModal && inputRef.current) {
            const focusInput = () => {
                if (inputRef.current) {
                    inputRef.current.focus();
                }
            };
            requestAnimationFrame(focusInput);
            setTimeout(focusInput, 100);
        }
    }, [openModal]);
    useEffect(() => {
        setShowValue(capitalizeFirstLetterAfterSpace(selectedLocation[0]));
    }, [selectedLocation]);
    // Render Components
    const renderLocationOption = (location, index) => {
        if (!location || location === "")
            return null;
        const isHovered = hoveredLocation && hoveredLocation.code === location.code;
        const isSelected = location.code === selectedLocation[1];
        return (_jsxs("li", { className: isHovered || isSelected ? "location-option location-option__hover" : "location-option", 
            // eslint-disable-next-line jsx-a11y/role-has-required-aria-props
            role: "option", onClick: () => closeModal(location), onFocus: () => setHoveredLocation(location), onBlur: () => setHoveredLocation(null), children: [_jsx("span", { className: "location-option__icon", children: getLocationIcon(location.location_type || location.placeType) }), _jsx("span", { className: "location-option__text", children: checkTranlation(location) })] }, index));
    };
    const renderEmptyState = () => {
        if (selectedLocation[0] === "") {
            return (_jsxs("li", { className: "location-option location-option--disabled", 
                // eslint-disable-next-line jsx-a11y/role-has-required-aria-props
                role: "option", "aria-disabled": "true", children: [_jsx("span", { className: "location-option__icon", children: _jsx(IconLocationBlack, {}) }), _jsx("span", { className: "location-option__text", children: t(isPickup ? "pickup_label_must" : "return_label_must") })] }));
        }
        return null;
    };
    const modalContent = (_jsxs(_Fragment, { children: [_jsx("div", { className: "modal-location", style: { height: viewportHeight }, children: _jsxs("div", { className: "modal-location__container", children: [_jsx(BlueBar, { onclick: () => isOpen(false), title: t(isPickup ? "pickup_label" : "return_label"), customId: "blue-bar-reservation", t: t }), _jsx("div", { className: "modal-location__header", children: _jsxs("div", { className: "modal-location__search-container", children: [_jsx("div", { className: "modal-location__title", children: t(isPickup ? "pickup_label" : "return_label") }), _jsx("div", { className: "modal-location__search", children: _jsxs("div", { className: "modal-location__input-wrapper", children: [_jsx("span", { className: "modal-location__input-icon", children: getLocationIcon(selectedLocation[2] || Constant.DEFAULT) }), _jsx("input", { ref: inputRef, id: isPickup ? "pickup_location" : "return_location", className: "modal-location__input", type: "text", value: showValue, autoComplete: "off", placeholder: t("location_input_placeholder"), onChange: (e) => {
                                                        setSelectedLocation([
                                                            e.target.value,
                                                            selectedLocation[1],
                                                            selectedLocation[2],
                                                        ]);
                                                        setFirstRender(false);
                                                        handleLocationChange(e, setNoResults);
                                                    }, onClick: handleDefaultClick, "aria-label": t(isPickup ? "pickup_label" : "return_label") }), _jsx("button", { className: "modal-location__clear-btn", onClick: handleClearLocation, "aria-label": t("aria_clear_location"), type: "button", children: _jsx(IconClosed, {}) })] }) })] }) }), _jsx("div", { className: "modal-location__results", children: _jsxs("ul", { className: "location-list", role: "listbox", style: { height: `calc(${viewportHeight}px - 140px)` }, children: [noResults && (
                                    // eslint-disable-next-line jsx-a11y/role-has-required-aria-props
                                    _jsx("li", { className: "location-list__message", role: "option", children: t("error_location") })), firstRender ? (renderEmptyState() || renderLocationOption(locations[0], "first-render")) : (locations.map((location, index) => renderLocationOption(location, index)))] }) })] }) }), _jsx("div", { className: "modal-location__backdrop", onClick: () => isOpen(false), children: "\u00A0" })] }));
    return createPortal(modalContent, document.body);
};
export default BodyModalLocations;
